import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { NotificationBanner, NotificationBannerProps } from './NotificationBanner';

export interface BannerNotification extends Omit<NotificationBannerProps, 'onDismiss'> {
  id: string;
  dismissible?: boolean;
}

interface NotificationContextValue {
  showBanner: (notification: Omit<BannerNotification, 'id'>) => string;
  dismissBanner: (id: string) => void;
  clearAllBanners: () => void;
}

const NotificationContext = createContext<NotificationContextValue | undefined>(undefined);

export function useNotification() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within NotificationProvider');
  }
  return context;
}

interface NotificationProviderProps {
  children: ReactNode;
}

export function NotificationProvider({ children }: NotificationProviderProps) {
  const [banners, setBanners] = useState<BannerNotification[]>([]);

  const showBanner = useCallback((notification: Omit<BannerNotification, 'id'>) => {
    const id = `banner-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const newBanner: BannerNotification = {
      ...notification,
      id,
      dismissible: notification.dismissible !== undefined ? notification.dismissible : true
    };

    setBanners(prev => [...prev, newBanner]);
    return id;
  }, []);

  const dismissBanner = useCallback((id: string) => {
    setBanners(prev => prev.filter(banner => banner.id !== id));
  }, []);

  const clearAllBanners = useCallback(() => {
    setBanners([]);
  }, []);

  return (
    <NotificationContext.Provider value={{ showBanner, dismissBanner, clearAllBanners }}>
      {children}
      {banners.length > 0 && (
        <div className="notification-banner-container" style={{
          position: 'fixed',
          top: '16px',
          right: '16px',
          zIndex: 1000,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          pointerEvents: 'none'
        }}>
          {banners.map(banner => (
            <div key={banner.id} style={{ pointerEvents: 'auto' }}>
              <NotificationBanner
                {...banner}
                onDismiss={banner.dismissible ? () => dismissBanner(banner.id) : undefined}
              />
            </div>
          ))}
        </div>
      )}
    </NotificationContext.Provider>
  );
}
